import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getHgbA1CForBHDXSummary } from '../HedisMetricService';
import HgbA1CForBHDXSummary from '../../../Models/HedisMetrics/HgbA1CForBHDXSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const HgbA1CForBHDXSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [hgbA1CForBHDXSummary, setHgbA1CForBHDXSummary] = useState<HgbA1CForBHDXSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        hgbA1cForBhDxHealthPlans,
        setHgbA1cForBhDxHealthPlans,
        hgbA1cForBhDxTeam,
        setHgbA1cForBhDxTeam,
        hgbA1cForBhDxDateFilter,
        setHgbA1cForBhDxDateFilter,
        hgbA1cForBhDxFromDate,
        setHgbA1cForBhDxFromDate,
        hgbA1cForBhDxToDate,
        setHgbA1cForBhDxToDate,
        hgbA1cForBhDxIncludePastOpenStatus,
        setHgbA1cForBhDxIncludePastOpenStatus,
        hgbA1cForBhDxShowInActiveTeam,
        setHgbA1cForBhDxShowInActiveTeam,
        setHedisType,
        setHealthPlan,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend(
                    'HgbA1C for BH DX Summary',
                    'Controlled vs Not Controlled',
                    '',
                    'Count',
                    [{ name: '', data: [0] }],
                    [''],
                    null,
                    chartHeight
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (hgbA1cForBhDxDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(hgbA1cForBhDxFromDate) && Boolean(hgbA1cForBhDxToDate))) {
            const changeFilter = async () => {
                let start = hgbA1cForBhDxFromDate !== null ? new Date(hgbA1cForBhDxFromDate).toDateString() : null;
                let end = hgbA1cForBhDxToDate !== null ? new Date(hgbA1cForBhDxToDate).toDateString() : null;
                var teamInfo = Boolean(hgbA1cForBhDxTeam) ? hgbA1cForBhDxTeam : 'All';
                setHgbA1cForBhDxTeam(teamInfo);
                const data = await getHgbA1CForBHDXSummary(
                    hgbA1cForBhDxHealthPlans,
                    teamInfo,
                    hgbA1cForBhDxDateFilter,
                    start,
                    end,
                    hgbA1cForBhDxIncludePastOpenStatus,
                    hgbA1cForBhDxShowInActiveTeam
                );
                setHgbA1CForBHDXSummary(data);
                setIsPrepared(true);
            };
            if ((hgbA1cForBhDxFromDate !== null || hgbA1cForBhDxToDate !== null) && hgbA1cForBhDxToDate < hgbA1cForBhDxFromDate) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'HgbA1C for BH DX Summary',
                        'Controlled vs Not Controlled',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hgbA1cForBhDxHealthPlans,
        hgbA1cForBhDxTeam,
        hgbA1cForBhDxDateFilter,
        hgbA1cForBhDxFromDate,
        hgbA1cForBhDxToDate,
        hgbA1cForBhDxIncludePastOpenStatus,
        hgbA1cForBhDxShowInActiveTeam,
    ]);

    useEffect(() => {
        if (hgbA1CForBHDXSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: hgbA1CForBHDXSummary.Controlled,
                name: 'Controlled',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Controlled', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: hgbA1CForBHDXSummary.NotControlled,
                name: 'Not Controlled',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Controlled', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            yValues = [];
            yVal = {
                y: hgbA1CForBHDXSummary.NotDocumented,
                name: 'Not Documented',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Documented', data: yValues, pointPadding: 0, color: '#4682B4', showInLegend: true });

            if (hgbA1CForBHDXSummary.Controlled === 0 && hgbA1CForBHDXSummary.NotControlled === 0 && hgbA1CForBHDXSummary.NotDocumented === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'HgbA1C for BH DX Summary',
                        'Controlled vs Not Controlled',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('HgbA1C for BH DX Summary', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hgbA1CForBHDXSummary, hgbA1cForBhDxHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlan(hgbA1cForBhDxHealthPlans);
        setTeam(hgbA1cForBhDxTeam);
        setFilter(hgbA1cForBhDxDateFilter);
        setFDate(hgbA1cForBhDxFromDate);
        setTDate(hgbA1cForBhDxToDate);
        setIncludePastOpenStatus(hgbA1cForBhDxIncludePastOpenStatus);
        setShowInActiveTeam(hgbA1cForBhDxShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.HgbA1cForBhDx);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/HgbA1cForBhDx`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            HgbA1C for BH DX
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">
                                        For members aged 18-64 who has an ICD 10 of F20.9, F25.0, F25.9, F31.32, F31.2, F31.3, F31.81, F31.89, F31.9
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Controlled :
                                    </Typography>

                                    <Typography variant="caption">Any of the CPT codes 3044F or 3051F</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Controlled :
                                    </Typography>
                                    <Typography variant="caption">Any of the CPT codes 3046F or 3052F</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Documented :
                                    </Typography>
                                    <Typography variant="caption">No CPT codes recorded</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={hgbA1cForBhDxTeam}
                        setSelectedTeam={setHgbA1cForBhDxTeam}
                        dateFilter={hgbA1cForBhDxDateFilter}
                        setDateFilter={setHgbA1cForBhDxDateFilter}
                        fromDate={hgbA1cForBhDxFromDate}
                        setFromDate={setHgbA1cForBhDxFromDate}
                        toDate={hgbA1cForBhDxToDate}
                        setToDate={setHgbA1cForBhDxToDate}
                        healthPlan={hgbA1cForBhDxHealthPlans}
                        setHealthPlan={setHgbA1cForBhDxHealthPlans}
                        includePastOpenStatusMembers={hgbA1cForBhDxIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setHgbA1cForBhDxIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.HgbA1cForBhDx}
                        showInActiveTeams={hgbA1cForBhDxShowInActiveTeam}
                        setShowInActiveTeams={setHgbA1cForBhDxShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default HgbA1CForBHDXSummaries;
